import {
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {ExpandLess, ExpandMore, InfoOutlined, Refresh} from '@material-ui/icons';
import {createSnackbar} from 'Core/ducks/snackbar';
import {isLoaded} from 'Core/models/Async';
import {getProposalListData, getQuotesForProposal} from 'Quoting/ducks/opportunity';
import {Proposal, ProposalQuote} from 'Quoting/models/DTOs/Proposal';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {getFormattedDate, getFormattedTime} from 'Quoting/utils/dateUtils';
import {getDocStatus, getProductCdFromModel, getProposalStatus} from 'Quoting/utils/helpers';
import {safeRenderValue} from 'Quoting/utils/utilities';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import ScrollToTop from 'Shared/components/ScrollToTop';
import {RequestMethod, openFile} from 'Shared/utilities/fetchHelpers';
import {capitalizeSubString} from 'Shared/utilities/stringFormatHelpers';
import {map as arrMap, reduce as arrReduce, flatten} from 'fp-ts/lib/Array';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {QuotingCard} from '../shared/QuotingCard/QuotingCard';
import {themeColors} from 'Theme';

const useProposalListStyles = makeStyles(() => ({
  header: {
    '& > * ': {
      paddingLeft: '2rem',
    },
    alignItems: 'center',
  },
  headerList: {
    width: '100%',
  },
  refreshProposalListBtn: {
    border: 'none',
    background: themeColors.brightBlue_100,
    color: themeColors.blue_600,
    fontWeight: 600,
    '&:hover': {
      border: 'none',
      backgroundColor: themeColors.brightBlue_100,
      color: themeColors.blue_600,
    },
    textTransform: 'capitalize',
    padding: '0.75rem 1.5rem',
  },
  proposalListTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 2rem',
    alignItems: 'center',
  },
  proposalListTitle: {
    fontWeight: 600,
    fontSize: '1rem',
  },
  proposalHeaderContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  proposalListInfoContainer: {
    display: 'flex',
    padding: '0 2rem',
    alignItems: 'center',
  },
}));

const useToolTipStyle = makeStyles(theme => ({
  tooltip: {
    fontSize: '0.875rem',
  },
}));

const ProposalDetailTitle = () => {
  const columnHeaders = [
    'Proposal',
    'Date Created',
    'Products',
    'Proposal Status',
    'Doc Generation Status',
    'Documents',
  ];

  const classes = useProposalListStyles();
  return (
    <List className={classes.headerList}>
      <ListItem>
        <Grid container alignItems="center" className={classes.header}>
          {columnHeaders.map(column => (
            <Grid item key={column} xs={2}>
              {column}
            </Grid>
          ))}
        </Grid>
      </ListItem>
    </List>
  );
};

const ViewProposalDocumentButton = ({
  docGenStatus,
  externalUrl,
}: {
  docGenStatus: string | null;
  externalUrl: string;
}) => {
  const dispatch = useDispatch();
  if (!docGenStatus || !externalUrl) {
    return null;
  }

  const handleProposalDocDownload = () => {
    const retrieveDocumentUrl = `api/documents/v1/documentContent`;
    const fileExtension = `pdf`;
    const successResponse = `Completed!`;
    dispatch(createSnackbar('Request initiated!'));

    const [docType, contentId] = externalUrl ? externalUrl.split('/').filter((_, index) => index > 1) : [];
    if (!docType || !contentId) {
      throw new Error('Invalid docType or contentId!');
    }
    const requestPayload = {
      retrieveContentRequest: {
        payload: {
          contentId: contentId,
          docType: docType,
        },
      },
    };
    openFile(retrieveDocumentUrl, fileExtension, successResponse, RequestMethod.POST, requestPayload)
      .then(() => dispatch(createSnackbar(successResponse)))
      .catch(() => dispatch(createSnackbar('Failed to download document!')));
  };

  return (
    <Link color="primary" onClick={handleProposalDocDownload} style={{cursor: 'pointer'}}>
      View Document
    </Link>
  );
};

const useProposalListViewStyles = makeStyles(() => ({
  root: {
    width: '100%',
  },
  linkedQuoteList: {
    paddingLeft: '4rem',
  },
}));

const ProposalQuoteList = ({
  proposalDetails,
  className,
  expanded,
}: {
  proposalDetails: Proposal;
  className?: string;
  expanded: boolean;
}) => {
  const {proposalQuotesMap} = useSelector((state: ReducerState) => state.opportunity);
  const dispatch = useDispatch();
  const linkedQuotes = proposalQuotesMap[proposalDetails.number];

  useEffect(() => {
    const shouldLoadLinkedQuotes =
      Array.isArray(proposalDetails.quotes) &&
      proposalDetails.quotes.length > 0 &&
      expanded &&
      linkedQuotes?.key !== 'Loading' &&
      !isLoaded(linkedQuotes);

    if (shouldLoadLinkedQuotes) {
      dispatch(getQuotesForProposal(proposalDetails.number, proposalDetails.quotes ?? []));
    }
  }, [expanded, proposalDetails.number, proposalDetails.quotes, linkedQuotes?.key]);

  return linkedQuotes ? (
    renderAsyncView(linkedQuotes, linkedQuoteList =>
      Array.isArray(linkedQuoteList) && linkedQuoteList.length > 0 ? (
        <List className={className}>
          {linkedQuoteList.map(quote => (
            <ListItem key={quote?.master?._key?.rootId}>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  <ListItemText
                    primary={getProductCdFromModel(quote?.master?._modelName as A360ProductModel)}
                    secondary={safeRenderValue(quote?.master?.policyNumber)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <ListItemText
                    primary="Last Change"
                    secondary={safeRenderValue(getFormattedDate(quote?.master?.accessTrackInfo?.updatedOn))}
                  />
                </Grid>
                <Grid item xs={2}>
                  <ListItemText
                    primary="Covers"
                    secondary={`${pipe(
                      quote?.rates?.ratePremiumEntry?.policy?.plans,
                      arrMap(p => p.coverages),
                      flatten,
                      arrReduce(0, (acc, cov) => acc + (cov?.rateCard?.numberOfLives ?? 0))
                    )} Lives`}
                  />
                </Grid>
                <Grid item xs={2}>
                  <ListItemText
                    primary="Plans"
                    secondary={safeRenderValue(quote?.master?.benefitPackagingDetail?.plans?.length)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <ListItemText
                    primary={safeRenderValue(capitalizeSubString(quote?.master?.state, 0, 1))}
                    secondary={`eff. ${getFormattedDate(quote?.master?.transactionDetails?.txEffectiveDate)}`}
                  />
                </Grid>
              </Grid>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography align="center">No Data</Typography>
      )
    )
  ) : (
    <Typography align="center">No Data</Typography>
  );
};

const ProposalListItem = ({proposal, className}: {proposal: Proposal; className?: string}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <React.Fragment>
      <ListItem>
        <ListItemIcon>
          <IconButton onClick={() => setExpanded(prev => !prev)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </ListItemIcon>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={2}>
            <ListItemText primary={proposal.name} secondary={proposal.number} />
          </Grid>
          <Grid item xs={2}>
            <ListItemText
              primary={getFormattedDate(proposal.createdOn, false)}
              secondary={getFormattedTime(proposal.createdOn, false)}
            />
          </Grid>
          <Grid item xs={2}>
            <ListItemText
              primary={`Quotes (${proposal.quotes?.length ?? 0})`}
              secondary={
                <Typography>
                  {safeRenderValue(proposal?.quotes?.map((quote: ProposalQuote) => quote.shortProductName).join(', '))}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={2}>
            <ListItemText primary={safeRenderValue(getProposalStatus(proposal.state))} />
          </Grid>
          <Grid item xs={2}>
            <ListItemText primary={safeRenderValue(getDocStatus(proposal.docGenStatus))} />
          </Grid>
          <Grid item xs={2}>
            {proposal.externalUrl && (
              <ViewProposalDocumentButton docGenStatus={proposal?.docGenStatus} externalUrl={proposal.externalUrl} />
            )}
          </Grid>
        </Grid>
      </ListItem>
      <Collapse in={expanded} timeout="auto">
        <ProposalQuoteList proposalDetails={proposal} className={className} expanded={expanded} />
      </Collapse>
    </React.Fragment>
  );
};

const ProposalListView = ({proposalList}: {proposalList: Proposal[]}) => {
  const classes = useProposalListViewStyles();
  return Array.isArray(proposalList) && proposalList.length > 0 ? (
    <List className={classes.root}>
      {proposalList.map(proposal => (
        <>
          <ProposalListItem proposal={proposal} key={proposal._key?.rootId} className={classes.linkedQuoteList} />
          <Divider />
        </>
      ))}
    </List>
  ) : (
    <Typography align="center">No Data</Typography>
  );
};

export const ProposalList = () => {
  const {proposals} = useSelector((state: ReducerState) => state.opportunity);
  const classes = useProposalListStyles();
  const dispatch = useDispatch();
  const toolTipClasses = useToolTipStyle();

  return (
    <div>
      <div className={classes.proposalHeaderContainer}>
        <div className={classes.proposalListTitleContainer}>
          <Typography className={classes.proposalListTitle}>Proposals</Typography>
          <Tooltip title={'Click for updated proposal status'} classes={toolTipClasses} tabIndex={0}>
            <Button
              onClick={() => dispatch(getProposalListData())}
              startIcon={<Refresh />}
              variant="outlined"
              className={classes.refreshProposalListBtn}
            >
              Refresh
            </Button>
          </Tooltip>
        </div>
        <div className={classes.proposalListInfoContainer}>
          <InfoOutlined color="primary" />
          <Typography style={{padding: '0 1rem'}}>Refresh for Proposal Generation Updates</Typography>
        </div>
      </div>
      <div style={{paddingBottom: '0px !important'}}>
        <QuotingCard title={<ProposalDetailTitle />} titleAsHeader>
          {renderAsyncView(proposals, proposalList => (
            <ProposalListView proposalList={proposalList} />
          ))}
        </QuotingCard>
      </div>
      <ScrollToTop />
    </div>
  );
};
